import { throttle } from "lodash-es";
import { useEffect, useState, useRef } from "react";

/**
 * Hook to determine if the user is scrolling down, based on accumulated scroll distance.
 * @param scrollRef Ref to the scrollable element.
 * @param bufferDistance Minimum scroll distance required to toggle the state.
 * @param throttleTime Throttle delay for the scroll handler.
 * @returns isScrollingDown (boolean) - Whether the user is scrolling down.
 */
export const useIsScrollingDown = (
  scrollRef: React.RefObject<HTMLElement>,
  bufferDistance = 50,
  throttleTime = 100
) => {
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const lastScrollTop = useRef(0); // Previous scroll position
  const scrollDelta = useRef(0); // Accumulated scroll delta

  useEffect(() => {
    const scrollElement = scrollRef.current;

    const handleScroll = throttle(() => {
      if (!scrollElement) return;

      const scrollTop = scrollElement.scrollTop;

      if (scrollTop > lastScrollTop.current) {
        // Scrolling down
        scrollDelta.current += scrollTop - lastScrollTop.current;
        if (scrollDelta.current > bufferDistance) {
          setIsScrollingDown(true);
          scrollDelta.current = 0; // Reset the delta
        }
      } else if (scrollTop < lastScrollTop.current) {
        // Scrolling up
        scrollDelta.current += lastScrollTop.current - scrollTop;
        if (scrollDelta.current > bufferDistance) {
          setIsScrollingDown(false);
          scrollDelta.current = 0; // Reset the delta
        }
      }

      lastScrollTop.current = Math.max(scrollTop, 0); // Avoid negative values
    }, throttleTime);

    scrollElement?.addEventListener("scroll", handleScroll);

    return () => {
      scrollElement?.removeEventListener("scroll", handleScroll);
    };
  }, [bufferDistance, throttleTime]);

  return isScrollingDown;
};